/**
 * The family's onboarding requests, as every family-side surface reads them (steward-app#1426,
 * steward-app#1427).
 *
 * Three surfaces render off `GET /onboarding-requests`: the notice on People → Family
 * (`OnboardingRequestNotices`), the Dashboard card (`OnboardingRequestCard`) and the dot on the
 * People nav item (`Layout`). All three decide "is this request open" with
 * {@link openOnboardingRequests} below and nothing else, so no two of them can disagree about
 * the same request. The rule itself is the façade's (`listFamilyOnboardingRequests`: open when
 * no confirmation has `confirmedAt >= lastIssuedAt`); this file reads its `open` flag and does
 * not re-derive it.
 *
 * Pure on purpose, like `src/feedback/unread.ts`: data in, data out, no query client and no
 * clock of its own. Every function that needs the time takes `now`.
 */

/** One open request, narrowed off the wire. */
export interface OpenOnboardingRequest {
  firmId: string;
  firmName: string;
  /**
   * When the firm last asked, or `null` when the value on the wire is not a readable instant.
   * Only the Dashboard card's snooze reads it; an unreadable value never re-surfaces a
   * snoozed card on its own, and never hides one either.
   */
  lastIssuedAt: string | null;
}

/** Epoch ms of an ISO instant, or `null` when absent or unreadable. */
function instant(value: unknown): number | null {
  if (typeof value !== 'string' || value === '') return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
}

/**
 * `GET /onboarding-requests`' body → the OPEN requests, or `null` when the body is not one this
 * build understands.
 *
 * Nothing on this path validates the payload (`api/client.ts` is a bare parse), and the SPA and
 * the façade deploy separately (steward-app#319), so every field is checked. An item that is not
 * a well-formed open request is left out rather than rendered with a blank firm name.
 *
 * `open` must be literally `true`. A body from a façade that stopped sending the flag therefore
 * reads as "nothing open", which costs a family a reminder, never a reminder that cannot clear.
 */
export function openOnboardingRequests(payload: unknown): OpenOnboardingRequest[] | null {
  if (payload === null || typeof payload !== 'object') return null;
  const items = (payload as Record<string, unknown>)['items'];
  if (!Array.isArray(items)) return null;
  const open: OpenOnboardingRequest[] = [];
  for (const item of items) {
    if (item === null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    if (r['open'] !== true) continue;
    if (typeof r['firmId'] !== 'string' || r['firmId'] === '') continue;
    if (typeof r['firmName'] !== 'string' || r['firmName'].trim() === '') continue;
    const lastIssuedAt = instant(r['lastIssuedAt']) === null ? null : r['lastIssuedAt'] as string;
    open.push({ firmId: r['firmId'], firmName: r['firmName'], lastIssuedAt });
  }
  return open;
}

/**
 * Whether the People nav dot shows: at least one request is open (founder decision on
 * steward-app#1427, 2026-09-16).
 *
 * It clears when the family confirms, never when someone looks: there is no seen marker, so
 * opening People does not quieten a request that another person made. A body this build cannot
 * read is `false`, the same "render nothing" posture the notice takes.
 */
export function hasOpenOnboardingRequest(payload: unknown): boolean {
  return (openOnboardingRequests(payload)?.length ?? 0) > 0;
}

/**
 * The People nav link's accessible name. The dot is `aria-hidden`, so without this a screen
 * reader hears a plain "People" link and the whole signal is invisible to it (the same reason
 * `feedbackNavLabel` exists).
 */
export function peopleNavLabel(requestWaiting: boolean): string {
  return requestWaiting ? 'People, an onboarding request is waiting' : 'People';
}

// ── "Remind me later" on the Dashboard card ─────────────────────────────────────────────

/** How long "Remind me later" hides the card. */
export const ONBOARDING_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Cap on stored snooze entries, across every workspace. The preferences blob has one 8KB budget
 * shared by every key (appcore answers `400 PREFERENCES_TOO_LARGE` past it). One entry
 * serialises to about `"<slug>":{"snoozedAt":"<24-char ISO>","lastIssuedAt":"<24-char ISO>"},`
 * plus its workspace key, under 150 bytes, so 20 is under 3KB even before pruning
 * (`requests.test.ts` pins that with long ids and slugs).
 */
export const ONBOARDING_SNOOZE_MAX_ENTRIES = 20;

/**
 * One firm's snooze.
 *
 * Two fields, compared against two different clocks, and never against each other:
 *
 * - `snoozedAt` is the BROWSER's clock at the press, and is only ever compared with the browser's
 *   clock again, for the three-day window.
 * - `lastIssuedAt` is the SERVER's `lastIssuedAt` for the request the person put off, copied
 *   verbatim off the card that was pressed. A re-issue is a CHANGE in that value, never "newer
 *   than `snoozedAt`": comparing a server instant with a browser instant would let a browser
 *   clock running behind show the card again for the very request that was put off, and one
 *   running ahead swallow a real re-issue. `null` when the card's value was unreadable.
 */
export interface OnboardingSnooze {
  snoozedAt: string;
  lastIssuedAt: string | null;
}

/**
 * `workspaceId` → `firmId` → the snooze.
 *
 * Keyed by WORKSPACE first, and that is a deliberate widening of the plain `{ [firmId]: … }`
 * shape. `preferences` hangs off the workspace-less user row, so it is shared by every
 * workspace the user belongs to, while a request belongs to one family. A person in two
 * families that the same firm serves would otherwise snooze both cards with one press, and the
 * pruning rule below (drop firms with no open request) would erase the other family's snooze
 * every time, because the open requests on hand are always the CURRENT workspace's. That second
 * failure is the one `pruneFeedbackSeen` records for `feedbackSeen`.
 */
export type OnboardingSnoozes = Record<string, Record<string, OnboardingSnooze>>;

/** The same issuance: both unreadable, or both readable and the same instant. */
function sameIssuance(a: string | null, b: string | null): boolean {
  return instant(a) === instant(b);
}

/**
 * True while "Remind me later" still hides this request's card.
 *
 * Hidden for {@link ONBOARDING_SNOOZE_MS} after `snoozedAt`, strictly: at exactly three days the
 * card is back. Shown again at once when the request's `lastIssuedAt` is not the one the person
 * put off, because a re-issue is a new request (see {@link OnboardingSnooze} for why that is a
 * comparison of server value with server value).
 *
 * Every unreadable input fails toward SHOWING the card. A snooze dated more than one window
 * into the future (a skewed clock, a hand-edited blob) is treated as no snooze. One dated less
 * than a window ahead is honoured, so the longest a bad value can hide a request is two windows
 * from now, never indefinitely.
 */
export function isOnboardingRequestSnoozed(
  request: OpenOnboardingRequest,
  snooze: OnboardingSnooze | undefined,
  now: number,
): boolean {
  if (snooze === undefined) return false;
  const snoozedAt = instant(snooze.snoozedAt);
  if (snoozedAt === null) return false;
  if (snoozedAt - now > ONBOARDING_SNOOZE_MS) return false;
  if (!sameIssuance(request.lastIssuedAt, snooze.lastIssuedAt)) return false;
  return now - snoozedAt < ONBOARDING_SNOOZE_MS;
}

/**
 * A value read back from the blob, narrowed into MAPS: anything that is not a map of maps is
 * ignored, and an entry without a readable `snoozedAt` or a string-or-null `lastIssuedAt` is
 * dropped.
 *
 * Maps rather than plain objects, so that an id such as `constructor`, `toString` or
 * `__proto__` is an ordinary key: a plain-object lookup of those returns an inherited member,
 * and a `??=` on one writes onto `Object` itself.
 */
function readSnoozes(value: unknown): Map<string, Map<string, OnboardingSnooze>> {
  const out = new Map<string, Map<string, OnboardingSnooze>>();
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return out;
  for (const [workspaceId, firms] of Object.entries(value as Record<string, unknown>)) {
    if (firms === null || typeof firms !== 'object' || Array.isArray(firms)) continue;
    const kept = new Map<string, OnboardingSnooze>();
    for (const [firmId, entry] of Object.entries(firms as Record<string, unknown>)) {
      if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) continue;
      const snoozedAt = (entry as Record<string, unknown>)['snoozedAt'];
      const lastIssuedAt = (entry as Record<string, unknown>)['lastIssuedAt'];
      if (typeof snoozedAt !== 'string' || instant(snoozedAt) === null) continue;
      if (typeof lastIssuedAt !== 'string' && lastIssuedAt !== null) continue;
      kept.set(firmId, { snoozedAt, lastIssuedAt });
    }
    if (kept.size > 0) out.set(workspaceId, kept);
  }
  return out;
}

/** The open requests the Dashboard card shows right now, in the order the façade listed them. */
export function visibleOnboardingRequests(
  open: readonly OpenOnboardingRequest[],
  /** The stored `onboardingSnoozed` preference, as read back: narrowed here, never trusted. */
  snoozes: unknown,
  workspaceId: string | null,
  now: number,
): OpenOnboardingRequest[] {
  const mine = workspaceId === null ? undefined : readSnoozes(snoozes).get(workspaceId);
  return open.filter(r => !isOnboardingRequestSnoozed(r, mine?.get(r.firmId), now));
}

/**
 * The next `onboardingSnoozed` value after "Remind me later" on `request` in `workspaceId`.
 *
 * `request` is the one on the card that was pressed, so the `lastIssuedAt` stored is the one the
 * person saw, not whatever the cache holds by the time a queued write runs.
 *
 * appcore replaces a top-level preference key wholesale, so the map returned here is exactly
 * what ends up stored, which is what makes pruning possible. Pruned on every write:
 *
 *  1. **In this workspace, firms with no open request, and ONLY when `open` is known.** `open` is
 *     this workspace's requests from a read that SUCCEEDED, or `null`. A quick workspace switch
 *     can reset that read before a queued write runs; treating "no data" as "nothing open" would
 *     then erase every live snooze in the family the person just pressed in. With `null` this
 *     workspace is pruned like any other, by rule 2 only.
 *  2. **Everywhere, snoozes whose window has passed**, and snoozes dated more than a window into
 *     the future. Neither hides anything whatever the requests do (see
 *     {@link isOnboardingRequestSnoozed}), so dropping them is lossless. This
 *     is what bounds the other workspaces, whose open requests this page cannot see.
 *  3. **A cap of {@link ONBOARDING_SNOOZE_MAX_ENTRIES}**, newest first, with the entry being
 *     written kept unconditionally.
 */
export function snoozeOnboardingRequest(
  current: unknown,
  open: readonly OpenOnboardingRequest[] | null,
  workspaceId: string,
  request: OpenOnboardingRequest,
  now: number,
): OnboardingSnoozes {
  const written = {
    workspaceId, firmId: request.firmId,
    snooze: { snoozedAt: new Date(now).toISOString(), lastIssuedAt: request.lastIssuedAt },
  };
  const openFirms = open === null ? null : new Set(open.map(r => r.firmId));
  const entries: Array<{ workspaceId: string; firmId: string; snooze: OnboardingSnooze; at: number }> = [];
  for (const [ws, firms] of readSnoozes(current)) {
    for (const [firm, snooze] of firms) {
      if (ws === workspaceId && firm === request.firmId) continue;
      if (ws === workspaceId && openFirms !== null && !openFirms.has(firm)) continue;
      const at = instant(snooze.snoozedAt)!;
      if (now - at >= ONBOARDING_SNOOZE_MS || at - now > ONBOARDING_SNOOZE_MS) continue;
      entries.push({ workspaceId: ws, firmId: firm, snooze, at });
    }
  }
  entries.sort((a, b) => b.at - a.at);
  const kept = [written, ...entries.slice(0, Math.max(0, ONBOARDING_SNOOZE_MAX_ENTRIES - 1))];
  const grouped = new Map<string, Array<[string, OnboardingSnooze]>>();
  for (const e of kept) {
    const list = grouped.get(e.workspaceId) ?? [];
    list.push([e.firmId, e.snooze]);
    grouped.set(e.workspaceId, list);
  }
  // `Object.fromEntries` defines OWN properties, so no key, `__proto__` included, reaches a
  // prototype on the way out either.
  return Object.fromEntries([...grouped].map(([ws, list]) => [ws, Object.fromEntries(list)]));
}
